# Posting a job ad

The SEEK API surfaces two mutations to post job ads, [`postPosition`](#postposition) and [`postPositionProfileForOpening`](#postpositionprofileforopening).
Posting typically takes less than a minute after which a [`PositionProfilePosted` event](#positionprofileposted) will be emitted.

Job ads on SEEK have a maximum period of 30 days.
Your software must allow hirers to advertise a job on SEEK for this full period.

## Idempotency

Requests that carry a job posting [mutation] must be [idempotent].
This removes the risk of unintentionally posting duplicate job ads when your software needs to [retry] a request due to an error.

A unique [`idempotencyId`] is required when posting a job ad.
We recommend that you generate a new [v4 UUID] before sending the first request to post each job ad, and to reuse that identifier on all subsequent retries of the same job ad.
This will ensure that we do not contact you unnecessarily about errored job ads that eventually succeed in going live on SEEK, as it provides a common thread across all attempts to post that job ad.

If your software has already posted a job ad with the same `idempotencyId`,
the SEEK API will return a [`PostPositionPayload_Conflict`] or [`PostPositionProfileForOpeningPayload_Conflict`] result in accordance with our [mutation conflict] convention.
If your software was retrying a job posting request,
you can treat the conflict result as a success as it indicates the job ad has already been posted.
See the [code samples below](#operations) for practical examples of these conflict results.

The SEEK API deduplicates job ads posted by your software based on their `idempotencyId`s.
Your software should never reuse an `idempotencyId` when posting a different job ad;
this may result in subsequent job ads being missed or other unexpected behaviour.
Your identifiers are isolated and will not conflict with those generated by other recruitment software providers.

[`PostPositionPayload_Conflict`]: /schema/named-type/PostPositionPayload_Conflict
[`PostPositionProfileForOpeningPayload_Conflict`]: /schema/named-type/PostPositionProfileForOpeningPayload_Conflict
[idempotent]: https://en.wikipedia.org/wiki/Idempotence#Computer_science_meaning
[mutation conflict]: /graphql/seek-api-conventions#mutation-conflicts
[mutation]: /graphql/in-practice#operations
[retry]: /graphql/error-responses#retrying-server-errors
[v4 UUID]: https://en.wikipedia.org/wiki/Universally_unique_identifier#Version_4_(random)

## Operations

The following sample mutations are provided for demonstration purposes only;
they are [not executable in the Playground environment](/graphql/playground).
You should use your live partner credentials to post a job ad under your [unsearchable SEEK test hirer](/introduction#integration-process) for end-to-end testing.

### postPosition

The [`postPosition` mutation] creates a new [position opening] and posts a [`PositionProfile`] in a single operation.

```graphql label="Mutation"
mutation ($input: PostPositionInput!) {
  postPosition(input: $input) {
    ... on PostPositionPayload_Success {
      positionOpening {
        documentId {
          value
        }
      }
      positionProfile {
        profileId {
          value
        }
      }
    }
    ... on PostPositionPayload_Conflict {
      conflictingPositionOpening {
        documentId {
          value
        }
      }
      conflictingPositionProfile {
        profileId {
          value
        }
      }
    }
  }
}
```

```jsonc label="Variables"
{
  "input": {
    // Details of the position opening to create along with the job ad
    "positionOpening": {
      "postingRequester": {
        "roleCode": "Company",

        // Hirer that will own the position opening
        "id": "seekAnzPublicTest:organization:seek:93WyyF1h",

        // Mary Manager is hiring this role as a hiring manager
        "personContacts": [
          {
            "name": { "formattedName": "Mary Manager" },
            "roleCode": "HiringManager",
            "communication": {
              "email": [{ "address": "mary.manager@example.com" }],
              "phone": [{ "formattedNumber": "03 8517 4100" }]
            }
          }
        ]
      }
    },

    // Details of the job ad itself
    "positionProfile": {
      "positionTitle": "Associate Developer",

      // Hirer we're posting the job ad on behalf of
      "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",

      "positionFormattedDescriptions": [
        // Short description to be shown in search results
        {
          "descriptionId": "SearchSummary",
          "content": "Start your software development career with a dynamic & growing team"
        },

        // Detailed description that appears on the job ad
        {
          "descriptionId": "AdvertisementDetails",
          "content": "A newly created role for an ambitious <b>junior</b> or <b>graduate</b> developer. Be part of a great team in the Bendigo area."
        }
      ],

      // 70—85 Australian dollars per hour
      "offeredRemunerationPackage": {
        "basisCode": "Hourly",
        "ranges": {
          "minimumAmount": {
            "value": 70,
            "currency": "AUD"
          },
          "maximumAmount": {
            "value": 85,
            "currency": "AUD"
          },
          "intervalCode": "Hour"
        },
        // No remuneration description will be displayed to the candidate
        "descriptions": []
      },

      "seekAnzWorkTypeCode": "PartTime",
      "seekWorkArrangementCodes": ["Remote"],

      // Information & Communication Technology > Developers/Programmers
      "jobCategories": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",

      // Australia > Victoria > Bendigo, Goldfields & Macedon Ranges > Argyle
      "positionLocation": "seekAnzPublicTest:location:seek:QVS62e6K",

      // Classic Ad with the default end date in 30 calendar days
      "postingInstructions": {
        "seekAdvertisementProductId": "globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFbCR3cKV9WdNSMC6e1vqiUJ",

        // See Idempotency section above
        "idempotencyId": "6eaeb58c-cc38-4b0e-8353-daa3011f1ece"
      }
    }
  }
}
```

```jsonc label="Success result"
{
  "postPosition": {
    // Details of the newly created position opening
    "positionOpening": {
      "documentId": {
        "value": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM"
      }
    },
    // Details of the newly created position profile
    "positionProfile": {
      "profileId": {
        "value": "seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb"
      }
    }
  }
}
```

```jsonc label="Conflict result"
{
  "postPosition": {
    // Details of the conflicting position opening
    "conflictingPositionOpening": {
      "documentId": {
        "value": "globalPublicTest:positionOpening:positions:E3Lz6HkSrbZCawh4gPGAtM"
      }
    },
    // Details of the conflicting position profile
    "conflictingPositionProfile": {
      "profileId": {
        "value": "seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb"
      }
    }
  }
}
```

### postPositionProfileForOpening

The [`postPositionProfileForOpening` mutation] posts a new [`PositionProfile`] within an existing [position opening].

A single position opening can contain a maximum of 300 position profiles.
Once a position profile has been posted it will remain in the position opening until 180 days after its [close date].

```graphql label="Mutation"
mutation ($input: PostPositionProfileForOpeningInput!) {
  postPositionProfileForOpening(input: $input) {
    ... on PostPositionProfileForOpeningPayload_Success {
      positionProfile {
        profileId {
          value
        }
      }
    }
    ... on PostPositionProfileForOpeningPayload_Conflict {
      conflictingPositionProfile {
        profileId {
          value
        }
      }
    }
  }
}
```

```jsonc label="Variables"
{
  "input": {
    "positionProfile": {
      "positionTitle": "Principal Developer",

      // Existing position opening created with `createPositionOpening` or `postPosition`
      "positionOpeningId": "globalPublicTest:positionOpening:positions:876y9hibtZZvv6vgtQx4zd",

      // Hirer we're posting the job ad on behalf of
      "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",

      "positionFormattedDescriptions": [
        // Short description to be shown in search results
        {
          "descriptionId": "SearchSummary",
          "content": "Experienced software developer wanted to lead an existing team."
        },

        // Three bullet points to display in search results
        {
          "descriptionId": "SearchBulletPoint",
          "content": "Line management required"
        },
        {
          "descriptionId": "SearchBulletPoint",
          "content": "Need experience with cloud deployments"
        },
        {
          "descriptionId": "SearchBulletPoint",
          "content": "Competitive benefits packages"
        },

        // Detailed description that appears on the job ad
        {
          "descriptionId": "AdvertisementDetails",
          "content": "<p>Acme Inc is looking for an experienced software developer with management experience to lead their cloud transformation.</p><p>You should bring your passion for both technology and people to this exciting role.</p>"
        }
      ],

      // 100,000–130,000 Australian dollars per year
      "offeredRemunerationPackage": {
        "basisCode": "Salaried",
        "ranges": {
          "minimumAmount": {
            "value": 100000,
            "currency": "AUD"
          },
          "maximumAmount": {
            "value": 130000,
            "currency": "AUD"
          },
          "intervalCode": "Year"
        },

        // Remuneration description visible to the candidate
        "descriptions": ["Up to $130k excluding super"]
      },

      "seekAnzWorkTypeCode": "FullTime",
      "seekWorkArrangementCodes": ["Remote"],

      // YouTube video to embed above the job ad's details
      "seekVideo": {
        "url": "https://www.youtube.com/watch?v=Q3UvigPHCjI",
        "seekAnzPositionCode": "Above"
      },

      // Information & Communication Technology > Developers/Programmers
      "jobCategories": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",

      // Australia > Queensland > Cairns & Far North Queensland > Cairns
      "positionLocation": "seekAnzPublicTest:location:seek:uxLiTMNo",

      // Premium Ad with the default end date in 30 calendar days
      "postingInstructions": {
        "seekAdvertisementProductId": "globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFaUj6LchctVWMg2zsCHxbpQ",

        // See Idempotency section above
        "idempotencyId": "6eaeb58c-cc38-4b0e-8353-daa3011f1ece"
      }
    }
  }
}
```

```jsonc label="Success result"
{
  "postPositionProfileForOpening": {
    // Details of the newly created position profile
    "positionProfile": {
      "profileId": {
        "value": "seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb"
      }
    }
  }
}
```

```jsonc label="Conflict result"
{
  "postPositionProfileForOpening": {
    // Details of the conflicting position profile
    "conflictingPositionProfile": {
      "profileId": {
        "value": "seekAnzPublicTest:positionProfile:jobAd:2ySKU6Urb"
      }
    }
  }
}
```

You will receive a [`FORBIDDEN` error] if the hirer in [`positionOrganizations`] does not match the [`PostingRequester.id`] of the existing [position opening].

[`forbidden` error]: /graphql/error-responses#forbidden
[`positionorganizations`]: /schema/named-type/PostPositionProfileForOpeningPositionProfileInput#/field/positionOrganizations
[`postingrequester.id`]: /schema/named-type/PostingRequester#/field/id

## Events

### PositionProfilePosted

You will receive events for SEEK hirers you have a [`JobPosting` relationship] with.

A [`PositionProfilePosted` event] is emitted when a job ad has been newly posted to the SEEK employment marketplace.
The [`PositionProfileUpdated` event] is emitted when an existing job ad is updated.

In response to these events you can [request fields from the position profile].
For example, you could query:

- The [`positionUri` field] to provide a hirer with a link to the live job ad.

- The [`postingInstructions.end` field] to retrieve the calculated end date if one wasn’t specified during posting.

[request fields from the position profile]: /use-cases/job-posting/managing-job-ads/querying-a-job-ad#positionprofile

This is an example of a [webhook](/events/webhooks) body containing a `PositionProfilePosted` event:

```jsonc sample-webhook-body
{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:UeFHntCu5giQh9Z6CRc2J7",
      "type": "PositionProfilePosted",
      "createDateTime": "2019-08-20T21:02:24.611Z",
      // This can be passed to the `positionProfile` query
      "positionProfileId": "seekAnzPublicTest:positionProfile:jobAd:26d9DVAzs",
      // This is only available for signed webhook subscriptions
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:Kyak8JfPKhHCAXM2s9b9wX",
  "url": "https://example.com/1/2/3",
  "expireDateTime": "2019-09-13T22:16:10.593Z"
}
```

[`idempotencyId`]: /schema/named-type/CreatePostingInstructionInput#/field/idempotencyId
[`JobPosting` relationship]: /auth/hirer-relationships
[`PositionProfile`]: /schema/named-type/PositionProfile
[`PositionProfilePosted` event]: /schema/webhooks/PositionProfilePostedEvent
[`PositionProfileUpdated` event]: /schema/webhooks/PositionProfileUpdatedEvent
[`positionUri` field]: /schema/named-type/PositionProfile#/field/positionUri
[`postingInstructions.end` field]: /schema/named-type/PostingInstruction#/field/end
[`postPosition` mutation]: /schema/mutation/postPosition
[`postPositionProfileForOpening` mutation]: /schema/mutation/postPositionProfileForOpening
[close date]: /use-cases/job-posting/key-concepts#job-ad-lifecycle
[position opening]: /use-cases/job-posting/position-openings
